import { isFuture, isSameDay, parseISO, subDays } from 'date-fns';
import {
  CreateMemberGroupRequest,
  GarnerMemberGroupOrchestrationServiceV2,
  UpdateMemberGroupRequest,
} from '@garner-health/api-client/svc-member-group-orchestration-v2';
import { GetClientMemberGroupsResponse } from '../../helpers/common';
import { getTimeFrameForPlan } from '../../helpers/plan-timeframe-helper';
import logger from '../../logging';
import { Member, MemberGroup } from '../../models';
import {
  coverageTierV2ToCoverageTierMap,
  Relationship,
  RelationshipToRelationshipV2,
  relationshipV2ToRelationshipMap,
} from '../../models/enums';
import { formatISODate } from '../../util';
import { compareMemberGroups, getClient, getServiceOpts, propsEqual } from '../api';

const log = logger(__filename);

function formatMemberGroup(memberGroup: GetClientMemberGroupsResponse): [Member, Member[]] {
  const primary = memberV2ToMember(
    memberGroup.members.find(member => member.relationship_code === RelationshipToRelationshipV2[Relationship.SELF])!,
  );
  const dependents = memberGroup.members
    .filter(member => member.relationship_code !== RelationshipToRelationshipV2[Relationship.SELF])
    .map(member => {
      const dependent = memberV2ToMember(member);
      dependent.isAddressSameAsPrimary = propsEqual(primary, dependent, [
        'addressLine1',
        'addressLine2',
        'city',
        'state',
        'zipCode',
      ]);
      return dependent;
    });

  return [primary, dependents];
}

function memberV2ToMember(member: GetClientMemberGroupsResponse['members'][number]): Member {
  const isEndDateInFuture = isFuture(parseISO(member.benefit_end_date));
  let benefitEndDate: string | undefined = member.benefit_end_date;

  let isCancelTerminated = false;
  const dayBeforeBenefitStartDate = formatISODate(subDays(parseISO(member.benefit_start_date), 1));

  if (member.benefit_end_date === dayBeforeBenefitStartDate) {
    benefitEndDate = undefined;
    isCancelTerminated = true;
  }

  const currentDate = new Date();
  const isEndDateToday = isSameDay(parseISO(member.benefit_end_date), currentDate);
  const isEndDateInThePast = !(isEndDateToday || isEndDateInFuture);
  const isPastTerminated = isEndDateInThePast;

  const relationshipCode = relationshipV2ToRelationshipMap.get(member.relationship_code);
  if (!relationshipCode) {
    log.error({ memberId: member.id }, 'member has invalid relationship code');
    throw new Error('member has invalid relationship code');
  }

  return {
    id: member.id,
    externalEmployeeId: member.employee_id,
    firstName: member.first_name,
    lastName: member.last_name,
    birthDate: member.date_of_birth ?? '',
    ssn: member.identification_code ?? '',
    email: member.email ?? undefined,
    phone: member.phone ?? undefined,
    addressLine1: member.address_line_1 ?? undefined,
    addressLine2: member.address_line_2 ?? undefined,
    city: member.city ?? undefined,
    state: member.state ?? undefined,
    zipCode:
      member.zip_code?.length === 9
        ? member.zip_code.slice(0, 5) + '-' + member.zip_code.slice(5)
        : member.zip_code ?? undefined,
    relationshipCode: relationshipCode,
    benefitStartDate: member.benefit_start_date,
    benefitEndDate: benefitEndDate,
    isCancelTerminated: isCancelTerminated,
    isPastTerminated: isPastTerminated,
  };
}

export async function getClientsFullTreeV2(clientId: number) {
  const [memberGroups, client] = await Promise.all([getClientMemberGroupsV2(clientId), getClient(clientId)]);
  const plans = client.plans.map(plan => ({
    id: plan.planId,
    name: plan.planName,
    startDate: plan.startDate,
    endDate: plan.endDate,
  }));

  if (!client.clientOrgs[0]) throw new Error('Missing client organization');
  const organizationId = client.clientOrgs[0].name;

  const groups = new Map<string, MemberGroup>();

  for (const mg of memberGroups) {
    const plan = plans.find(p => p.id === mg.plan_id);
    if (!plan) {
      log.warn(
        { memberGroupId: mg.id, planId: mg.plan_id },
        'Member group plan ID does not match to a client plan. Skipping',
      );
      continue;
    }

    const [primary, dependents] = formatMemberGroup(mg);

    let size = 1;
    const currentDate = new Date();
    dependents.forEach(dependent => {
      if (
        (!dependent.benefitEndDate ||
          isFuture(parseISO(dependent.benefitEndDate)) ||
          isSameDay(parseISO(dependent.benefitEndDate), currentDate)) &&
        !dependent.isCancelTerminated
      ) {
        size++;
      }
    });

    groups.set(mg.id, {
      id: mg.id,
      clientId: mg.client_id,
      coverageTier: coverageTierV2ToCoverageTierMap.get(mg.coverage_tier),
      benefitPlanTimeFrame: getTimeFrameForPlan(plan),
      benefitPlanName: plan.name,
      benefitPlanId: mg.plan_id,
      invoiceGroup: mg.invoice_group ?? undefined,
      organizationId: organizationId,
      primary,
      dependents,
      size,
      clientOrganizationId: mg.client_organization_id?.toString() ?? undefined,
      subOrganizationId: mg.sub_organization_id ?? undefined,
      reportingGroup: mg.reporting_group ?? undefined,
    });
  }

  return {
    id: client.id,
    name: client.name,
    organizationId,
    plans,
    memberGroups: Array.from(groups.values()).sort(compareMemberGroups),
  };
}

export async function getClientMemberGroupsV2(clientId: number) {
  const memberService = new GarnerMemberGroupOrchestrationServiceV2(await getServiceOpts());
  log.info({ clientId }, 'before calling GET /member-groups');
  return memberService
    .get('/member-groups', {
      query: {
        client_id: clientId,
        offset: 0,
        limit: 99999,
      },
    })
    .onStatus(200, r => {
      log.info({ clientId, context: r }, 'response info from GET /member-groups');
      return r.json.member_groups;
    });
}

export async function createMemberGroup(apiMemberGroup: CreateMemberGroupRequest['json']) {
  const memberService = new GarnerMemberGroupOrchestrationServiceV2(await getServiceOpts());

  return memberService
    .post('/member-groups', {
      query: { client_id: apiMemberGroup.client_id },
      json: apiMemberGroup,
    })
    .onStatus(201, r => r.headers['location'])
    .onStatus(400, ({ json: { message } }) => {
      log.error(message);
      throw new Error(message);
    });
}

export async function updateMemberGroup(memberGroupId: string, apiMemberGroup: UpdateMemberGroupRequest['json']) {
  const memberService = new GarnerMemberGroupOrchestrationServiceV2(await getServiceOpts());

  return memberService
    .put('/member-groups/{member_group_id}', {
      query: { client_id: apiMemberGroup.client_id },
      params: {
        member_group_id: memberGroupId,
      },
      json: apiMemberGroup,
    })
    .onStatus(200, () => memberGroupId)
    .onStatus(400, ({ json: { message } }) => {
      log.error(message);
      throw new Error(message);
    });
}
